/*
 * @Author: Ryan
 * @Date: 2026-01-23
 * @Description: 对比表格模块 - 展示 Video Agent 与传统方案的差异
 */
import React from 'react';

import styles from './index.module.scss';

/** 对比列配置 */
export interface ComparisonColumn {
  /** 列 ID */
  id: string;
  /** 上方小标签 */
  label: string;
  /** 主标题 */
  title: string;
  /** 是否高亮（Next Gen 列） */
  highlighted?: boolean;
}

/** 对比行配置 */
export interface ComparisonRow {
  /** 行标题（左侧） */
  feature: string;
  /** 各列对应的值 */
  values: string[];
}

export interface ComparisonTableSectionProps {
  /** 模块标题 */
  title: string;
  /** 模块副标题 */
  subtitle?: string;
  /** 列配置 */
  columns: ComparisonColumn[];
  /** 行数据 */
  rows: ComparisonRow[];
}

const ComparisonTableSection: React.FC<ComparisonTableSectionProps> = ({
  title,
  subtitle,
  columns,
  rows,
}) => {
  return (
    <section className={styles.section}>
      <div className={styles.container}>
        {/* 标题区域 */}
        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        </div>

        {/* 表格区域 */}
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            {/* 表头 */}
            <thead>
              <tr>
                <th className={styles.featureHeader} scope="col">
                  <span className="sr-only">Feature</span>
                </th>
                {columns.map((col) => (
                  <th
                    key={col.id}
                    scope="col"
                    className={`${styles.columnHeader} ${col.highlighted ? styles.highlighted : ''}`}
                  >
                    <span className={styles.label}>{col.label}</span>
                    <span className={styles.columnTitle}>{col.title}</span>
                  </th>
                ))}
              </tr>
            </thead>

            {/* 表体 */}
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex} className={styles.row}>
                  <th scope="row" className={styles.featureCell}>
                    {row.feature}
                  </th>
                  {row.values.map((value, colIndex) => (
                    <td
                      key={colIndex}
                      className={`${styles.valueCell} ${columns[colIndex]?.highlighted ? styles.highlightedCell : ''}`}
                    >
                      {value}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default ComparisonTableSection;
